/**
 * Cita Events - Checkout Core API Module
 * =======================================
 * Unified API Endpoints for all Checkout Implementations
 *
 * This module provides:
 * - All checkout-related API endpoints
 * - Unified apiCall wrapper with error handling
 * - Helper functions for common operations
 *
 * @version 1.0.0
 * @module checkout-core/api
 */

import { fetchJson, normalizeApiResponse } from '../../http/utils.js';

const __ = window.__ || function(s) { return s; };

/**
 * API base path for Cita Events endpoints.
 * @constant {string}
 */
export const API_BASE = '/api/method/cita_events.api';

/**
 * All checkout-related API endpoints.
 * Organized by domain: registration, payment, utilities.
 *
 * @constant {Object}
 */
export const endpoints = {
  // =====================
  // Guest Registration
  // =====================
  registerGuestForTicket: `${API_BASE}.event.guest_registration_api.register_guest_for_ticket`,
  createCheckoutForGuest: `${API_BASE}.payments.stripe_checkout.create_checkout_for_guest`,

  // =====================
  // User Registration
  // =====================
  registerForEvent: `${API_BASE}.event.event_user_api.register_for_event`,
  createCheckoutForTicket: `${API_BASE}.payments.stripe_checkout.create_checkout_for_ticket`,
  requestTicketApproval: `${API_BASE}.event.event_user_api.request_ticket_approval`,
  deleteRegistration: `${API_BASE}.event.event_user_api.delete_registration`,
  cancelRegistration: `${API_BASE}.event.event_user_api.cancel_my_registration`,

  // =====================
  // Plus-One
  // =====================
  addPlusOne: `${API_BASE}.event.guest_registration_api.add_plus_one`,

  // =====================
  // Waitlist
  // =====================
  joinWaitlist: `${API_BASE}.event.event_user_api.join_waitlist`,

  // =====================
  // Payment
  // =====================
  resumeCheckout: `${API_BASE}.payments.stripe_checkout.resume_checkout_for_registration`,
  validatePromoCode: `${API_BASE}.payments.stripe_checkout.validate_promo_code`,
  getWalletAvailability: `${API_BASE}.payments.stripe_checkout.get_wallet_availability`,
  createPaymentIntentForWallet: `${API_BASE}.payments.stripe_checkout.create_payment_intent_for_wallet`,
  confirmWalletPayment: `${API_BASE}.payments.stripe_checkout.confirm_wallet_payment`,
  // =====================
  // Utilities
  // =====================
  emailHasAccount: `${API_BASE}.event.event_public_api.email_has_account`,
  getBotProtectionConfig: `${API_BASE}.event.event_public_api.get_bot_protection_config`,
  getTicketPdf: `${API_BASE}.event.pdf_ticket.get_ticket_pdf`,
};

/**
 * Default timeout for API calls in milliseconds.
 * @constant {number}
 */
const DEFAULT_TIMEOUT = 15000;

/**
 * Make an API call with unified error handling.
 *
 * @param {string} endpoint - The API endpoint URL
 * @param {Object} [data={}] - Request payload
 * @param {Object} [options={}] - Request options
 * @param {string} [options.method='POST'] - HTTP method
 * @param {number} [options.timeout=15000] - Request timeout in ms
 * @returns {Promise<Object>} Canonical response envelope
 */
export async function apiCall(endpoint, data = {}, options = {}) {
  const { method = 'POST', timeout = DEFAULT_TIMEOUT, idempotencyKey } = options;

  try {
    const fetchOptions = {
      method,
      timeout,
    };

    if (idempotencyKey) {
      fetchOptions.headers = { 'X-Idempotency-Key': idempotencyKey };
    }

    // For GET requests, don't send body
    if (method === 'GET') {
      // Append data as query params if provided
      if (data && Object.keys(data).length > 0) {
        const params = new URLSearchParams();
        Object.entries(data).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, String(value));
          }
        });
        endpoint = `${endpoint}?${params.toString()}`;
      }
    } else {
      fetchOptions.body = data;
    }

    const { res, payload } = await fetchJson(endpoint, fetchOptions);
    const normalized = normalizeApiResponse(payload, { response: res });

    return {
      success: normalized.success,
      code: normalized.code,
      toast: normalized.toast,
      data: normalized.data || {},
    };
  } catch (error) {
    return {
      success: false,
      code: 'fetch_failed',
      toast: __('Ein Netzwerkfehler ist aufgetreten. Bitte versuche es erneut.'),
      data: {},
    };
  }
}

// =====================
// Convenience Functions
// =====================

/**
 * Check if an email address has an existing account.
 *
 * @param {string} email - Email to check
 * @returns {Promise<boolean>} True if account exists
 */
export async function checkEmailHasAccount(email) {
  if (!email) return false;

  const result = await apiCall(endpoints.emailHasAccount, { email });
  return result.success && result.data?.exists === true;
}

/**
 * Validate a promo code for a ticket.
 *
 * @param {Object} params - Validation parameters
 * @param {string} params.event - Event name
 * @param {string} params.ticket_type - Ticket type name
 * @param {string} params.code - Promo code to validate
 * @param {number} [params.quantity] - Ticket quantity
 * @param {string} [params.tier_id] - Selected pricing tier
 * @param {string} [params.email] - Buyer email for guest-aware validation
 * @param {string} [params.membership_number] - Membership number for stacked pricing
 * @param {string} [params.invite_token] - Invite token
 * @param {string} [params.invite_code] - Invite code
 * @param {string} [params.access_code] - Hidden-ticket access code
 * @param {string} [params.captcha_token] - Promo abuse/captcha token
 * @returns {Promise<Object>} Validation result with discount info
 */
export async function validatePromoCode({
  event,
  ticket_type,
  code,
  quantity,
  tier_id,
  email,
  membership_number,
  invite_token,
  invite_code,
  access_code,
  captcha_token,
}) {
  return await apiCall(endpoints.validatePromoCode, {
    event,
    ticket_type,
    promo_code: code,
    quantity,
    tier_id,
    email,
    membership_number,
    invite_token,
    invite_code,
    access_code,
    captcha_token,
  });
}

/**
 * Resume checkout for an existing registration.
 *
 * NOTE (#1076 R2 dead-code audit, 2026-06-20): this checkout-core helper has NO
 * current caller — the live resume-with-consent path is resume.html's own inline
 * fetch, and the in-page button now navigates to /checkout/resume/<reg>. It is
 * KEPT as the stable public barrel export (re-exported via index.js); the
 * consent plumbing below is retained intentionally so that any future caller of
 * the public API cannot reintroduce the terms-active charge dead-end the #1076
 * fix closes. Removing it from the public surface is out of scope for this NIT.
 *
 * #1076 (ANN): on terms-active events the resume endpoint requires the
 * organizer-terms boolean to unblock the charge guard. The client supplies
 * ONLY this boolean; version/snapshot/content_hash are server-derived under
 * the resume lock (the server forces False on inactive-terms events).
 *
 * @param {string} registration - Registration ID
 * @param {Object} [opts]
 * @param {boolean} [opts.consentOrganizerTerms] - Organizer-terms acceptance.
 * @returns {Promise<Object>} Result with checkout_url if successful
 */
export async function resumeCheckout(registration, opts = {}) {
  const body = { registration };
  if (opts && opts.consentOrganizerTerms === true) {
    body.consent_organizer_terms = true;
  }
  return await apiCall(endpoints.resumeCheckout, body);
}

/**
 * Add a plus-one to an existing registration.
 *
 * @param {Object} params - Plus-one parameters
 * @param {string} params.registration - Parent registration ID
 * @param {string} params.first_name - First name
 * @param {string} params.last_name - Last name
 * @param {string} params.email - Email address
 * @param {string} [params.birth_date] - Birth date (YYYY-MM-DD)
 * @param {boolean} [params.age_confirmed] - Age confirmation checkbox
 * @param {string} [params.access_token] - Access token for guest registrations
 * @returns {Promise<Object>} Result with plus_one_registration if successful
 */
export async function addPlusOne(params) {
  return await apiCall(endpoints.addPlusOne, params);
}

/**
 * Cancel a registration.
 *
 * @param {string} registration - Registration ID to cancel
 * @returns {Promise<Object>} Cancellation result
 */
export async function cancelRegistration(registration) {
  return await apiCall(endpoints.cancelRegistration, { registration });
}

/**
 * Join event waitlist.
 *
 * @param {Object} params - Waitlist parameters
 * @param {string} params.event - Event name
 * @param {string} params.ticket_type - Ticket type name
 * @returns {Promise<Object>} Waitlist result
 */
export async function joinWaitlist({ event, ticket_type }) {
  return await apiCall(endpoints.joinWaitlist, { event, ticket_type });
}

/**
 * Get bot protection configuration for an event.
 *
 * @param {Object} params - Configuration parameters
 * @param {string} [params.event] - Event name
 * @param {string} [params.slug] - Event slug
 * @param {string} [params.invite_token] - Invite token
 * @param {string} [params.invite_code] - Invite code
 * @returns {Promise<Object>} Bot protection config
 */
export async function getBotProtectionConfig(params = {}) {
  const result = await apiCall(endpoints.getBotProtectionConfig, params, { method: 'GET' });
  if (result.success) {
    return result.data;
  }
  return { enabled: false, mode: 'none' };
}

export default {
  API_BASE,
  endpoints,
  apiCall,
  checkEmailHasAccount,
  validatePromoCode,
  resumeCheckout,
  addPlusOne,
  cancelRegistration,
  joinWaitlist,
  getBotProtectionConfig,
};
